import { EnvironmentStatusDot } from "@/components/environments/EnvironmentStatusDot";
import { cn } from "@/components/podkit/lib/cn";
import { useGetEditorIdForEnv } from "@/hooks/use-editor-for-env";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useListEditors } from "@/queries/editor-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";
import { effectiveState } from "@/routes/environments/phase";
import { statusText } from "@/utils/environment";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const AgentExecutionActiveEnvironment: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const navigate = useNavigate();

    const environmentId = agentExecution.status?.usedEnvironments[0]?.environmentId;
    const { data: environment } = useEnvironment(environmentId);

    const { editorId } = useGetEditorIdForEnv(environmentId);
    const { data: editors } = useListEditors();
    const selectedEditor = editors?.editors.find((editor) => editor.id === editorId) || editors?.editors[0];

    const handleViewDetails = useCallback(() => {
        if (!environment) {
            return;
        }
        navigate(getDetailsURL({ environment }));
    }, [navigate, environment]);

    if (!environment) {
        return null;
    }

    return (
        <EnvironmentActionsDropdown
            environment={environment}
            defaultEditor={selectedEditor}
            handleViewDetails={handleViewDetails}
            button={
                <button
                    className={cn(
                        // we mimic our Pill component styling here. We can't use it directly (even if slotting was implemented) because we already are in a slot.
                        "inline-flex items-center gap-1 border-[0.5px] border-border-base font-bold",
                        "!rounded-[20px]", // why !important? Because the className of the containing slot is put first when merging, thereby overriding this value (https://github.com/radix-ui/primitives/issues/2631)
                        "px-1.5 py-0.5 text-xs",
                        "bg-surface-03 text-content-secondary",
                        "h-6 select-none overflow-hidden",
                    )}
                >
                    <EnvironmentStatusDot className="shrink-0" size="base" env={environment} />
                    <span className="min-w-0 shrink truncate">{statusText(effectiveState(environment))}</span>
                </button>
            }
        />
    );
};
